import { UPLOAD_CHUNK_BYTES, UPLOAD_CHUNK_RETRIES } from '@/constants/app';
import { getLocal } from '@/db/repo';

/**
 * Moves Library file bytes to and from the server in chunks.
 *
 * Chunking is not an optimisation here, it is the only way: a browser cannot
 * write to Netlify Blobs directly, and a synchronous function caps both its
 * request and its response at 6 MB.
 *
 * Uploads resume by asking the server which chunks it already has, so a
 * half-finished upload costs only what is missing — no local bookkeeping to go
 * stale.
 */

export interface TransferProgress {
  done: number;
  total: number;
}

export class TransferError extends Error {}

async function endpoint(name: string): Promise<string> {
  const local = await getLocal();
  const base = local.serverUrl.replace(/\/+$/, '');
  return `${base}/.netlify/functions/${name}`;
}

async function authHeader(): Promise<Record<string, string>> {
  const local = await getLocal();
  if (!local.token) throw new TransferError('Sign in on the Sync screen before adding files.');
  return { authorization: `Bearer ${local.token}` };
}

export function chunkCount(size: number): number {
  return Math.max(1, Math.ceil(size / UPLOAD_CHUNK_BYTES));
}

export async function serverChunks(fileId: string): Promise<number[]> {
  const url = `${await endpoint('file-status')}?id=${encodeURIComponent(fileId)}`;
  const response = await fetch(url, { headers: await authHeader() });
  if (!response.ok) throw new TransferError(`Could not check the upload (${response.status}).`);
  const body = (await response.json()) as { chunks: number[] };
  return body.chunks ?? [];
}

async function putChunk(fileId: string, index: number, body: Blob): Promise<void> {
  const url = `${await endpoint('file-chunk')}?id=${encodeURIComponent(fileId)}&i=${index}`;
  const headers = { ...(await authHeader()), 'content-type': 'application/octet-stream' };

  let lastError: unknown;
  for (let attempt = 0; attempt < UPLOAD_CHUNK_RETRIES; attempt += 1) {
    try {
      const response = await fetch(url, { method: 'PUT', headers, body });
      if (response.ok) return;
      // A refusal on content will not get better by trying again.
      if (response.status === 400 || response.status === 401 || response.status === 413) {
        const detail = (await response.json().catch(() => ({}))) as { error?: string };
        throw new TransferError(`Upload refused: ${detail.error ?? response.status}`);
      }
      lastError = new TransferError(`Chunk ${index} failed (${response.status}).`);
    } catch (err) {
      if (err instanceof TransferError) throw err;
      lastError = err;
    }
    await new Promise((r) => setTimeout(r, 400 * 2 ** attempt));
  }
  throw new TransferError(
    `Chunk ${index} did not go through: ${(lastError as Error)?.message ?? 'unknown error'}`,
  );
}

/** Uploads whatever is missing. Safe to call again after a failure. */
export async function uploadFile(
  fileId: string,
  blob: Blob,
  onProgress?: (p: TransferProgress) => void,
): Promise<void> {
  const total = chunkCount(blob.size);
  const already = new Set(await serverChunks(fileId));

  let done = already.size;
  onProgress?.({ done, total });

  for (let i = 0; i < total; i += 1) {
    if (already.has(i)) continue;
    const start = i * UPLOAD_CHUNK_BYTES;
    await putChunk(fileId, i, blob.slice(start, Math.min(start + UPLOAD_CHUNK_BYTES, blob.size)));
    done += 1;
    onProgress?.({ done, total });
  }
}

/** Fetches every chunk and reassembles the file locally. */
export async function downloadFile(
  fileId: string,
  size: number,
  mime: string,
  onProgress?: (p: TransferProgress) => void,
): Promise<Blob> {
  const total = chunkCount(size);
  const parts: BlobPart[] = [];

  for (let i = 0; i < total; i += 1) {
    const url = `${await endpoint('file-chunk')}?id=${encodeURIComponent(fileId)}&i=${i}`;
    let got: ArrayBuffer | null = null;
    let lastStatus = 0;

    for (let attempt = 0; attempt < UPLOAD_CHUNK_RETRIES; attempt += 1) {
      try {
        const response = await fetch(url, { headers: await authHeader() });
        if (response.ok) {
          got = await response.arrayBuffer();
          break;
        }
        lastStatus = response.status;
        if (response.status === 404 || response.status === 401) break;
      } catch {
        // network blip; fall through to the backoff
      }
      await new Promise((r) => setTimeout(r, 400 * 2 ** attempt));
    }

    if (!got) {
      throw new TransferError(
        lastStatus === 404
          ? 'This file is not on the server — it may not have finished uploading.'
          : `Could not download part ${i + 1} of ${total}.`,
      );
    }
    // Wrapped rather than pushed raw: a bare ArrayBuffer is a valid BlobPart in
    // browsers but not everywhere, and a view costs nothing.
    parts.push(new Uint8Array(got));
    onProgress?.({ done: i + 1, total });
  }

  return new Blob(parts, { type: mime });
}

export async function deleteRemoteFile(fileId: string): Promise<void> {
  const url = `${await endpoint('file-chunk')}?id=${encodeURIComponent(fileId)}`;
  const response = await fetch(url, { method: 'DELETE', headers: await authHeader() });
  if (!response.ok && response.status !== 404) {
    throw new TransferError(`Could not remove the stored file (${response.status}).`);
  }
}
